import { type NavigateFunction } from 'react-router-dom';

/** Lazy routes (e.g. Home) often mount after one frame; a fixed delay reliably misses the element. */
const HASH_SCROLL_RETRY_MS = 5000;

/**
 * When returning to the library with a target section, we skip ScrollToTop once: it uses scrollTo(0,0)
 * so a new route does not open where the previous page left the viewport. That same call races
 * scrollIntoView and can erase the section scroll — suppress for this navigation only.
 */
let pendingScrollToTopSuppression = false;

export function consumeScrollToTopSuppression(): boolean {
  if (!pendingScrollToTopSuppression) return false;
  pendingScrollToTopSuppression = false;
  return true;
}

/* The fragment a navigation is still scrolling to, such as 'section-social'. */
let pendingFragment = '';

/** The element id a back link is on its way to ('' when none), so Home can open on that section instead of its intro. */
export function pendingHashTarget(): string {
  return pendingFragment;
}

export function navigateWithOptionalHash(navigate: NavigateFunction, target: string) {
  if (!target.includes('#')) {
    navigate(target, { viewTransition: true });
    return;
  }

  const [path, fragment] = target.split('#');
  if (!fragment) {
    navigate(path || '/', { viewTransition: true });
    return;
  }

  const pathname = path || '/';
  pendingScrollToTopSuppression = true;
  pendingFragment = fragment;
  navigate(pathname, { viewTransition: true });

  const start = performance.now();
  const tryScroll = () => {
    const el = document.getElementById(fragment);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      pendingFragment = '';
      return;
    }
    if (performance.now() - start < HASH_SCROLL_RETRY_MS) {
      requestAnimationFrame(tryScroll);
    } else {
      pendingFragment = '';
    }
  };

  requestAnimationFrame(tryScroll);
}
